import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../services/api';

export interface Async<T> {
  data?: T;
  error?: ApiError;
  loading: boolean;
  reload: () => void;
}

/** Run `load` whenever `deps` change, ignoring answers that arrive after a newer request began. */
export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): Async<T> {
  const [state, setState] = useState<{
    deps: readonly unknown[];
    data?: T;
    error?: ApiError;
    loading: boolean;
  }>({ deps, loading: true });
  const [tick, setTick] = useState(0);
  const latest = useRef(0);
  const sameDeps = (previous: readonly unknown[]) =>
    previous.length === deps.length && previous.every((value, index) => Object.is(value, deps[index]));

  useEffect(() => {
    const id = ++latest.current;
    setState((previous) => ({
      deps,
      data: sameDeps(previous.deps) ? previous.data : undefined,
      loading: true,
    }));
    load().then(
      (data) => {
        if (id === latest.current) setState({ deps, data, loading: false });
      },
      (error: unknown) => {
        if (id === latest.current) {
          setState({
            deps,
            error: error instanceof ApiError ? error : new ApiError(0, 'error', String(error)),
            loading: false,
          });
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      if (latest.current === id) latest.current++;
    };
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return sameDeps(state.deps)
    ? { data: state.data, error: state.error, loading: state.loading, reload }
    : { loading: true, reload };
}

export function useTitle(title: string): void {
  useEffect(() => {
    document.title = title ? `${title} · Athenaeum Library` : 'Athenaeum Library';
  }, [title]);
}

export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}
